import { lazy } from "react";

import { AppRoutes } from "../types/enums/appRoutes";
import {
  AI_FEEDBACK_ALLOWED_ROLES,
  CHATBOT_ALLOWED_ROLES,
  CRITICAL_GAP_APPROVAL_ALLOWED_ROLES,
  DEAL_ONBOARDING_ALLOWED_ROLES,
  DOCUMENT_VIEW_ALLOWED_ROLES,
  PLANNING_VIEW_ALLOWED_ROLES,
  PROMPT_VIEW_ALLOWED_ROLES,
  RAID_VIEW_ALLOWED_ROLES,
  USER_ACCESS_ALLOWED_ROLES,
  WORKFLOW_TRACE_ALLOWED_ROLES,
} from "../types/enums/roleKeys";
import type { IAppRoute } from "../types/routesConfig";
import lazyImportRetry from "../utils/routeUtil/lazyImportRetry";
import { env } from "./env";

const HomeRouter = lazy(() =>
  lazyImportRetry(() => import("../pages/homePage/HomeRouter"), "HomeRouter")
);
const OnboardingPage = lazy(() =>
  lazyImportRetry(() => import("../pages/onboardingPage/OnboardingPage"), "OnboardingPage")
);
const AboutPage = lazy(() =>
  lazyImportRetry(() => import("../pages/aboutPage/AboutPage"), "AboutPage")
);
const PlanningPage = lazy(() =>
  lazyImportRetry(() => import("../pages/planningPage/PlanningPage"), "PlanningPage")
);
const ChecklistPage = lazy(() =>
  lazyImportRetry(() => import("../pages/checklistPage/ChecklistPage"), "ChecklistPage")
);
const GapApprovalPage = lazy(() =>
  lazyImportRetry(() => import("../pages/gapApprovalPage/GapApprovalPage"), "GapApprovalPage")
);
const ValuePage = lazy(() =>
  lazyImportRetry(() => import("../pages/valuePage/ValuePage"), "ValuePage")
);
const ImoSummaryPage = lazy(() =>
  lazyImportRetry(() => import("../pages/imoSummaryPage/ImoSummaryPage"), "ImoSummaryPage")
);
const SteerCoPage = lazy(() =>
  lazyImportRetry(() => import("../pages/steerCoPage/SteerCoPage"), "SteerCoPage")
);
const SettingsPage = lazy(() =>
  lazyImportRetry(() => import("../pages/settingsPage/SettingsPage"), "SettingsPage")
);
const DealDetailsPage = lazy(() =>
  lazyImportRetry(() => import("../pages/dealDetailsPage/DealDetailsPage"), "DealDetailsPage")
);
const UserSettingsPage = lazy(() =>
  lazyImportRetry(() => import("../pages/userSettingsPage/UserSettingsPage"), "UserSettingsPage")
);
const UserAccessPage = lazy(() =>
  lazyImportRetry(() => import("../pages/userAccessPage/UserAccessPage"), "UserAccessPage")
);
const ValidateMappingsPage = lazy(() =>
  lazyImportRetry(() => import("../pages/validateMappingsPage/ValidateMappingsPage"), "ValidateMappingsPage")
);
const ProjectDocumentsPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/additionalDocumentsPage/AdditionalDocumentsPage"),
    "ProjectDocumentsPage"
  )
);
const PromptManagementPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/promptManagementPage/PromptManagementPage"),
    "PromptManagementPage"
  )
);
const AiInsightsTestPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/aiInsightsTestPage/AiInsightsTestPage"),
    "AiInsightsTestPage"
  )
);
const AiInsightsDevPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/aiInsightsDevPage/AiInsightsDevPage"),
    "AiInsightsDevPage"
  )
);
const AiFeedbackDashboardPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/aiFeedbackDashboardPage/AiFeedbackDashboardPage"),
    "AiFeedbackDashboardPage"
  )
);
const AiTracesPage = lazy(() =>
  lazyImportRetry(() => import("../pages/aiTracesPage/AiTracesPage"), "AiTracesPage")
);
const AiTraceDetailPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/aiTracesPage/AiTraceDetailPage"),
    "AiTraceDetailPage"
  )
);
const PromptDetailPage = lazy(() =>
  lazyImportRetry(() => import("../pages/promptDetailPage/PromptDetailPage"), "PromptDetailPage")
);
const PromptTemplateDetailPage = lazy(() =>
  lazyImportRetry(
    () => import("../pages/promptTemplateDetailPage/PromptTemplateDetailPage"),
    "PromptTemplateDetailPage"
  )
);
const IntegrationPlanningPage = lazy(() =>
  lazyImportRetry(() => import("../pages/integrationPlanningPage/IntegrationPlanningPage"), "IntegrationPlanningPage")
);
const IntegrationPlanningDetailsPage = lazy(() =>
  lazyImportRetry(() => import("../pages/integrationPlanningDetailsPage/IntegrationPlanningDetailsPage"), "IntegrationPlanningDetailsPage")
);
const RaidPage = lazy(() =>
  lazyImportRetry(() => import("../pages/raidPage/RaidPage"), "RaidPage")
);
const DecisionsLogPage = lazy(() =>
  lazyImportRetry(() => import("../pages/decisionsLogPage/DecisionsLogPage"), "DecisionsLogPage")
);
const AiChatPage = lazy(() =>
  lazyImportRetry(() => import("../pages/aiChatPage/AiChatPage"), "AiChatPage")
);
const ExamplesPage = lazy(() =>
  lazyImportRetry(() => import("../pages/examplesPage/ExamplesPage"), "ExamplesPage")
);
const NotFoundPage = lazy(() =>
  lazyImportRetry(() => import("../pages/notFoundPage/NotFoundPage"), "NotFoundPage")
);
const ForbiddenPage = lazy(() =>
  lazyImportRetry(() => import("../pages/forbiddenPage/ForbiddenPage"), "ForbiddenPage")
);

/**
 * Flat application route list.
 * Add new routes here; the router in Layout picks them up automatically.
 */
const appRouteList: IAppRoute[] = [
  // Home ("/") is deal-status aware: HomeRouter shows the deal create/resume
  // landing page until a deal is onboarded (active), then Integration Planning.
  { path: AppRoutes.home, element: HomeRouter, roles: "*" },
  { path: AppRoutes.onboarding, element: OnboardingPage, roles: [...DEAL_ONBOARDING_ALLOWED_ROLES] },
  { path: AppRoutes.about, element: AboutPage, roles: "*", isMemo: true },
  { path: AppRoutes.planning, element: PlanningPage, roles: [...PLANNING_VIEW_ALLOWED_ROLES], requiresDeal: true },
  { path: AppRoutes.checklist, element: ChecklistPage, roles: [...PLANNING_VIEW_ALLOWED_ROLES], requiresDeal: true },
  // Approval review is restricted: it is the only surface that discloses critical gaps before the
  // engagement team has signed them off.
  {
    path: AppRoutes.criticalGapRequirementApproval,
    element: GapApprovalPage,
    roles: [...CRITICAL_GAP_APPROVAL_ALLOWED_ROLES],
    requiresDeal: true,
  },
  { path: AppRoutes.value, element: ValuePage, roles: "*", requiresDeal: true },
  { path: AppRoutes.imoSummary, element: ImoSummaryPage, roles: "*", requiresDeal: true },
  { path: AppRoutes.steerCo, element: SteerCoPage, roles: "*", requiresDeal: true },
  { path: AppRoutes.settings, element: SettingsPage, roles: "*" },
  {
    path: AppRoutes.settingsDealDetails,
    element: DealDetailsPage,
    roles: [...PLANNING_VIEW_ALLOWED_ROLES],
    requiresDeal: true,
  },
  { path: AppRoutes.settingsUserSettings, element: UserSettingsPage, roles: "*" },
  { path: AppRoutes.settingsUserAccess, element: UserAccessPage, roles: [...USER_ACCESS_ALLOWED_ROLES] },
  {
    path: AppRoutes.settingsValidateMappings,
    element: ValidateMappingsPage,
    roles: [...PLANNING_VIEW_ALLOWED_ROLES],
    requiresDeal: true,
  },
  {
    path: AppRoutes.settingsProjectDocuments,
    element: ProjectDocumentsPage,
    roles: [...DOCUMENT_VIEW_ALLOWED_ROLES],
    requiresDeal: true,
  },
  { path: AppRoutes.settingsPrompts, element: PromptManagementPage, roles: [...PROMPT_VIEW_ALLOWED_ROLES] },
  { path: AppRoutes.settingsAiFeedback, element: AiFeedbackDashboardPage, roles: [...AI_FEEDBACK_ALLOWED_ROLES] },
  { path: AppRoutes.settingsAiTraces, element: AiTracesPage, roles: [...WORKFLOW_TRACE_ALLOWED_ROLES] },
  { path: AppRoutes.settingsAiTraceDetail, element: AiTraceDetailPage, roles: [...WORKFLOW_TRACE_ALLOWED_ROLES] },
  // Internal AI Insights test harness -- only registered when
  // env.enableAiInsightsTestPage is true (local dev, or dev/test deploys with
  // VITE_ENABLE_AI_INSIGHTS_TEST_PAGE=true). Omitted entirely from stg/prod
  // builds so the route 404s there instead of just being hidden from the UI.
  ...(env.enableAiInsightsTestPage
    ? [{ path: AppRoutes.settingsAiInsightsTest, element: AiInsightsTestPage, roles: "*" } as IAppRoute]
    : []),
  ...(env.enableAiInsightsDevPage
    ? [{ path: AppRoutes.settingsAiInsightsDev, element: AiInsightsDevPage, roles: "*" } as IAppRoute]
    : []),
  {
    path: AppRoutes.settingsTemplateDetail,
    element: PromptTemplateDetailPage,
    roles: [...PROMPT_VIEW_ALLOWED_ROLES],
  },
  {
    path: AppRoutes.settingsPromptDetail,
    element: PromptDetailPage,
    roles: [...PROMPT_VIEW_ALLOWED_ROLES],
  },
  {
    path: AppRoutes.integrationPlanning,
    element: IntegrationPlanningPage,
    roles: [...PLANNING_VIEW_ALLOWED_ROLES],
    requiresDeal: true,
  },
  {
    path: AppRoutes.integrationPlanningDetails,
    element: IntegrationPlanningDetailsPage,
    roles: [...PLANNING_VIEW_ALLOWED_ROLES],
    requiresDeal: true,
  },
  { path: AppRoutes.raid, element: RaidPage, roles: [...RAID_VIEW_ALLOWED_ROLES], requiresDeal: true },
  { path: AppRoutes.decisionsLog, element: DecisionsLogPage, roles: [...RAID_VIEW_ALLOWED_ROLES], requiresDeal: true },
  // AI Chat can be disabled in selected environments via
  // VITE_ENABLE_CHATBOT_MENU=false, which removes this route entirely.
  ...(env.enableChatbotMenu
    ? [
        {
          path: AppRoutes.aiChat,
          element: AiChatPage,
          roles: [...CHATBOT_ALLOWED_ROLES],
          requiresDeal: true,
        } as IAppRoute,
      ]
    : []),
  { path: AppRoutes.examples, element: ExamplesPage, roles: "*" },
  { path: AppRoutes.forbidden, element: ForbiddenPage, roles: "*", isMemo: true },
  { path: AppRoutes.notFound, element: NotFoundPage, roles: "*", isMemo: true },
];

export default appRouteList;
